import { useCallback } from 'react'
import { useMutation } from '@apollo/client/react'
import { rrulestr } from '@guestgoodie/helpers'
import { useLingui } from '@lingui/react/macro'

import {
  AdminAPI,
  getFragment,
  GraphQL,
  SharedAPI,
  utils
} from '@guestgoodie/graphql'

import * as Form from '@/features/form'
import { GoodieFile, useUpdateFiles } from '@/features/files'
import { showToast, ToastType } from '@/features/toast'
import { formToGraphql, FormTranslations } from '@/features/translations'

export type FormData = {
  isActive: boolean
  name: string
  title: string
  description: string
  overrideLabel: string
  discountedPrice: number
  recurrences: GraphQL.RecurrenceFragmentFragment[]
  files: GoodieFile[]
  banners: GraphQL.BannerFragmentFragment[]
  priority: number
  translations: FormTranslations
}

export const TRANSLATABLE_FIELDS: (keyof FormData)[] = [
  'title',
  'description',
  'overrideLabel'
]

interface Options {
  goodieId: number
  id?: number
  onDone?: (
    promotion?: GraphQL.PromotionFragmentFragment,
    deleted?: boolean
  ) => void
}

function timeFromRecurrences(
  recurrences: GraphQL.RecurrenceFragmentFragment[]
) {
  return recurrences.reduce<{ startAt: string | null; endAt: string | null }>(
    function (acc, rec) {
      // Set startAt
      const start = new Date(rec.startDate)

      if (!acc.startAt) {
        acc.startAt = start.toISOString()
      } else {
        const currStart = new Date(acc.startAt)

        if (start < currStart) acc.startAt = start.toISOString()
      }

      // Set endAt
      const rule = rrulestr(rec.rrule)
      const end = rule.options?.until

      if (!end) return acc

      if (!acc.endAt) {
        acc.endAt = end.toISOString()
      } else {
        const currEnd = new Date(acc.endAt)

        if (end > currEnd) acc.endAt = end.toISOString()
      }

      return acc
    },
    {
      startAt: new Date().toISOString(),
      endAt: null
    }
  )
}

export default function usePromotionForm(options: Options) {
  const { goodieId, id, onDone } = options

  const { t } = useLingui()

  const [createPromotion] = useMutation(AdminAPI.promotion.CREATE, {
    refetchQueries: [AdminAPI.promotion.QUERY]
  })

  const [updatePromotion] = useMutation(AdminAPI.promotion.UPDATE, {
    refetchQueries: [
      {
        query: AdminAPI.promotion.GET,
        variables: {
          id,
          withGallery: true,
          withTranslations: true
        }
      }
    ]
  })

  const [deletePromotion] = useMutation(AdminAPI.promotion.DELETE, {
    update(cache, { data }) {
      if (!data) return

      const promotion = getFragment(
        SharedAPI.promotion.PROMOTION_FRAGMENT,
        data.deletePromotion
      )

      utils.paginationHelpers.remove(
        cache,
        promotion.id,
        'Promotion',
        'queryPromotions'
      )
    }
  })

  const [updateFiles, { progress }] = useUpdateFiles()

  const { errors, handlePromiseCatch } = Form.useErrorHelpers({})

  const create = useCallback(
    async ({ files, ...data }: FormData) => {
      return updateFiles(files)
        .then(files => files.map(({ id }) => id))
        .then(gallery => {
          return createPromotion({
            variables: {
              input: {
                goodieId,
                ...data,
                gallery,
                banners: data.banners.map(b => b.id),
                ...timeFromRecurrences(data.recurrences),
                translations: formToGraphql(data.translations)
              }
            }
          })
        })
        .then(({ data, error }) => {
          if (error) return Promise.reject(error)
          if (!data) return Promise.reject('unknown')

          showToast({
            type: ToastType.SUCCESS,
            id: 'success-promotion-create',
            message: t({
              comment:
                'Success toaster message when creating a promotion worked',
              message: 'Successfully created the promotion'
            })
          })

          const promotion = getFragment(
            SharedAPI.promotion.PROMOTION_FRAGMENT,
            data.createPromotion
          )

          onDone?.(promotion)
        })
        .catch(error =>
          handlePromiseCatch(error, {
            unknownString: t({
              comment:
                'Error message when the creating of a promotion doesn`t work',
              message:
                "There was a problem creating the promotion, please try again or if it repeatedly doesn't work, write our support."
            })
          })
        )
    },
    [onDone, createPromotion, updateFiles, t, goodieId, handlePromiseCatch]
  )

  const update = useCallback(
    async ({ files, ...data }: FormData) => {
      if (!id) return

      return updateFiles(files)
        .then(files => files.map(({ id }) => id))
        .then(gallery => {
          return updatePromotion({
            variables: {
              input: {
                ...data,
                id,
                gallery,
                banners: data.banners.map(b => b.id),
                ...timeFromRecurrences(data.recurrences),
                translations: formToGraphql(data.translations)
              }
            }
          })
        })
        .then(({ data, error }) => {
          if (error) return Promise.reject(error)
          if (!data) return Promise.reject('unknown')

          showToast({
            type: ToastType.SUCCESS,
            id: 'success-promotion-update',
            message: t({
              comment:
                'Success toaster message when updating a promotion worked',
              message: 'Successfully updated the promotion'
            })
          })

          const promotion = getFragment(
            SharedAPI.promotion.PROMOTION_FRAGMENT,
            data.updatePromotion
          )

          onDone?.(promotion)
        })
        .catch(error =>
          handlePromiseCatch(error, {
            unknownString: t({
              comment:
                'Error message when the creating of a promotion doesn`t work',
              message:
                "There was a problem creating the promotion, please try again or if it repeatedly doesn't work, write our support."
            })
          })
        )
    },
    [onDone, updatePromotion, updateFiles, t, handlePromiseCatch, id]
  )

  const remove = useCallback(() => {
    if (!id) return

    deletePromotion({ variables: { id } })
    onDone?.(undefined, true)
  }, [id, onDone, deletePromotion])

  return {
    errors,
    create,
    update,
    remove,
    progress
  }
}
